/**
 * mapBackendProposal — TASK-396
 *
 * Convert a saved backend proposal (market_data.portfolio_proposals, flat
 * `transactions`) into the proposalStore `Proposal` shape (nested `Trade[]`).
 * This is the ONLY flat→nested conversion in the flow; it lets SavedProposalCard
 * render balance + ImpactMetrics for both human- and AI-authored proposals.
 */

import type { Proposal, Trade } from '../../../../stores/proposalStore';

interface BackendTransaction {
  type: 'buy' | 'sell' | 'add_cash';
  symbol?: string;
  name?: string;
  quantity?: number;
  price?: number;
  currency?: string;
  amount?: number;
}

export interface BackendProposal {
  id: string;
  proposal_name?: string;
  proposal_type?: string;
  transactions?: BackendTransaction[];
  rationale?: string;
  advisor_notes?: string;
  created_by?: string;
  status?: string;
  created_at?: string;
  updated_at?: string;
}

// Backend status vocabulary → store's three-state badge. Cosmetic only.
const STATUS_MAP: Record<string, Proposal['status']> = {
  implemented: 'applied',
  approved: 'applied',
  pending_review: 'ready',
  ready: 'ready',
};

function toTrade(txn: BackendTransaction, index: number): Trade {
  const quantity = txn.quantity ?? 0;
  const estimatedPrice = txn.price ?? 0;
  return {
    id: `trade_${index}`,
    action: txn.type === 'sell' ? 'sell' : 'buy',
    instrument: {
      isin: txn.symbol ?? '',
      name: txn.name ?? txn.symbol ?? '',
      type: '',
      currency: txn.currency,
    },
    quantity,
    estimatedPrice,
    estimatedValue: txn.amount ?? quantity * estimatedPrice,
  };
}

export function mapBackendProposal(bp: BackendProposal): Proposal {
  const timestamp = bp.created_at ?? new Date().toISOString();
  return {
    id: bp.id,
    title: bp.proposal_name ?? 'Proposal',
    category: bp.proposal_type ?? 'rebalancing',
    rationale: bp.rationale || bp.advisor_notes || '',
    // add_cash carries no security to trade; drop it from the trade list (balance/impact ignore cash).
    trades: (bp.transactions ?? []).filter((t) => t.type !== 'add_cash').map(toTrade),
    status: STATUS_MAP[bp.status ?? ''] ?? 'draft',
    // ponytail: backend has no ai/user column — the advisor saved it via the composer, so 'user'.
    // True AI attribution needs threading createdBy through saveProposal + a DB column (follow-up).
    createdBy: 'user',
    lastModifiedBy: 'user',
    createdAt: timestamp,
    updatedAt: bp.updated_at ?? timestamp,
  };
}
